/**
 * Canonical "working day" definition for the Dashboard.
 *
 * A working day excludes:
 *   - Saturdays and Sundays
 *   - Italian public holidays (utils/holidays — Pasquetta + Lodi
 *     patron saint included)
 *   - Admin-flagged company closures (the `closures` table)
 *
 * Every average and percentage on the dashboard normalizes against
 * this count, so a 31-day month with 8 holidays is comparable to a
 * 28-day month with 1 holiday. Importing these helpers (instead of
 * redefining "workday" inline per widget) is what keeps the rule
 * consistent across the company and person views, and across
 * month-over-month comparisons.
 */
import type { ClosureMap } from "../types";
import { dateKey, daysInMonth, isWeekend } from "./dates";
import { getHoliday } from "./holidays";

/** True iff (year, month, day) counts as a working day for dashboard
 *  averaging purposes. month is 0-indexed. */
export function isWorkday(
  year: number,
  month: number,
  day: number,
  closures: ClosureMap,
): boolean {
  if (isWeekend(year, month, day)) return false;
  const key = dateKey(year, month, day);
  if (closures[key] !== undefined) return false;
  if (getHoliday(key) !== null) return false;
  return true;
}

/** Returns the list of working day-of-month numbers (1-indexed) in the
 *  given month. The length is the canonical denominator for all
 *  "average per working day" or "% of person-workdays" calculations. */
export function workdaysOfMonth(
  year: number,
  month: number,
  closures: ClosureMap,
): number[] {
  const out: number[] = [];
  const n = daysInMonth(year, month);
  for (let d = 1; d <= n; d++) {
    if (isWorkday(year, month, d, closures)) out.push(d);
  }
  return out;
}

/** Iterate (year, month) pairs from start to end inclusive. Useful for
 *  the Dashboard's date range — the caller can compute per-month
 *  aggregates and combine them. */
export function monthsInRange(
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number,
): Array<{ year: number; month: number }> {
  const out: Array<{ year: number; month: number }> = [];
  let y = startYear;
  let m = startMonth;
  // Guard against inverted ranges by swapping if needed
  const startKey = startYear * 12 + startMonth;
  const endKey = endYear * 12 + endMonth;
  if (startKey > endKey) {
    return monthsInRange(endYear, endMonth, startYear, startMonth);
  }
  while (y < endYear || (y === endYear && m <= endMonth)) {
    out.push({ year: y, month: m });
    m++;
    if (m > 11) {
      m = 0;
      y++;
    }
  }
  return out;
}
